import { Box, Typography } from '@mui/material';
import { useState } from 'react';
import type { Edge } from 'reactflow';
import type { McpToolItem } from '../../../service/McpServerService';
import { isActiveEdge } from '../../../util/findActiveHandle';
import type { User } from '../../../util/Types/User';
import type { McpNodeFormState } from '../../../types/mcpNode';
import TagInput from '../../Test/TagInput';
import {
  MCP_TAG_INPUT_WIDTH,
  MCP_TAG_PROMPT_HEIGHT,
} from './mcpNodeUiConstants';

export interface McpCommandPanelProps {
  user: User | null;
  nodeId: string;
  edges: Edge[];
  data: Record<string, unknown>;
  formData: McpNodeFormState;
  availableTools: McpToolItem[];
  onChange: (e: { target: { name: string; value: string } }) => void;
  findColor: Record<string, string> | undefined;
}

const McpCommandPanel = ({
  user,
  nodeId,
  edges,
  data,
  formData,
  availableTools,
  onChange,
  findColor,
}: McpCommandPanelProps) => {
  const [toolContextOpen, setToolContextOpen] = useState(false);

  return (
    <Box>
      <Typography
        variant="caption"
        fontWeight={700}
        color="text.secondary"
        sx={{ display: 'block', mt: 2, mb: 1.5 }}
      >
        Command
      </Typography>
      {formData.toolName && availableTools.length > 0 && (
        <Box sx={{ mb: 1.5 }} className="nodrag">
          <Box
            onClick={() => setToolContextOpen(prev => !prev)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              px: 1.5,
              py: 0.75,
              borderRadius: toolContextOpen ? '4px 4px 0 0' : 1,
              bgcolor:
                user?.theme === 'dark'
                  ? 'rgba(255,255,255,0.06)'
                  : 'grey.100',
              userSelect: 'none',
            }}
          >
            <Typography
              variant="caption"
              fontWeight={600}
              color="text.secondary"
            >
              Tool context
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: '0.65rem', lineHeight: 1 }}
            >
              {toolContextOpen ? '▲' : '▼'}
            </Typography>
          </Box>
          {toolContextOpen && (
            <Box
              sx={{
                maxHeight: 130,
                overflowY: 'auto',
                px: 1.5,
                pt: 1,
                pb: 1.25,
                borderRadius: '0 0 4px 4px',
                bgcolor:
                  user?.theme === 'dark'
                    ? 'rgba(255,255,255,0.06)'
                    : 'grey.100',
              }}
              className="nowheel"
            >
              {(() => {
                const tool = availableTools.find(
                  t => t.name === formData.toolName,
                );
                if (!tool) return null;
                return (
                  <Typography
                    variant="caption"
                    component="pre"
                    sx={{
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontFamily: 'monospace',
                      fontSize: '0.8125rem',
                      lineHeight: 1.5,
                      m: 0,
                    }}
                  >
                    {tool.description ?? tool.name}
                    {tool.inputSchema &&
                      Object.keys(tool.inputSchema).length > 0 &&
                      `\nSchema: ${JSON.stringify(tool.inputSchema)}`}
                  </Typography>
                );
              })()}
            </Box>
          )}
        </Box>
      )}
      <TagInput
        name="prompt"
        placeholder="Describe the command in natural language"
        labelName="Prompt"
        handleId="prompt"
        width={MCP_TAG_INPUT_WIDTH}
        height={MCP_TAG_PROMPT_HEIGHT}
        maxGrowHeight={240}
        isCollapsed={true}
        nodeId={nodeId}
        isActive={isActiveEdge(edges, nodeId, 'prompt', 'target')}
        isAdvanced={true}
        findColor={findColor}
        removeIcons
        advancedCollapsed={true}
        onChange={onChange}
        dataConnected={(data?.promptData as { text: string }[]) || []}
        placement={data?.placement as string | undefined}
        value={formData.prompt}
        nodeLabel={data?.label as string | undefined}
      />
    </Box>
  );
};

export default McpCommandPanel;
